<template>
  <div>
    <DsForm
      v-show="!isFormSuccess"
      :fields="formFields"
      :submit-state="formSubmitState"
      submit-label="Submit"
      submit-success-label="Success"
      submit-fail-label="Try Again"
      submit-color="black"
      @submit="handleSubmit"
      @change="handleFormChange"
    />
    <!-- Form success message -->
    <div
      v-if="isFormSuccess"
      class="ds-rich-text"
    >
      <h2 class="ds-heading-small ds-margin-bottom-12">Form Submitted Correctly!</h2>
      <p class="ds-text ds-margin-bottom-8">
        Thank you for reaching out to Adyen with a data privacy request. We have received your request.
      </p>
      <p class="ds-text ds-margin-bottom-8">
        Your identity needs to be confirmed. Please check your email for confirmation, click confirm and we will start
        your request.
      </p>
      <p class="ds-text ds-margin-bottom-8">
        If you would like to know more about the way Adyen collects and processes personal data, please take a look at
        the <a href="https://www.adyen.com/policies-and-disclaimer/privacy-policy">Adyen privacy statement</a>.
      </p>
    </div>
  </div>
</template>

<script setup>
import { DsForm, FORM_SUBMIT_STATE as DsFormSubmitState } from 'awds'
import * as Messages from '../../core/messages'

const { initRecaptcha, recaptcha } = useRecaptcha()
const { isFormSubmitStateSuccess: isFormSuccess } = useForms()
const { mergeLocales } = useLocales()
const router = useRouter()

const props = defineProps({
  formSuccessRedirectPath: {
    type: String,
    default: null
  }
})

const messages = mergeLocales([Messages])

/**
 * If any changes are necessary on requestTypesOptions and subjectTypesOptions,
 * please check on handleFormChange, since there are field dependencies.
 */
const requestTypesOptionsBase = [
  {
    id: 'delete',
    value: 'RequestType4',
    text: 'Delete my data'
  },
  {
    id: 'get',
    value: 'RequestType3',
    text: 'Get a copy of my data'
  },
  {
    id: 'update',
    value: 'RequestType2',
    text: 'Update my data'
  },
  {
    id: 'optOut',
    value: 'RequestType1',
    text: 'Opt out'
  },
  {
    id: 'objectToProcessing',
    value: 'RequestType5',
    text: 'Object to Processing'
  }
]

const requestTypesOptions = [
  ...requestTypesOptionsBase,
  {
    id: 'doNotSellMyInformation',
    value: 'RequestType10',
    text: 'Do Not Sell My Information'
  }
]

const requestTypesCustomOptions = [
  ...requestTypesOptionsBase,
  {
    id: 'reviewAutomatedDecision',
    value: 'RequestType7',
    text: 'Review Automated Decision'
  },
  {
    id: 'optOutForAutomatedDecisionMakingUtilizedInAdyenCapital',
    value: 'RequestType12',
    text: 'Opt-out for automated decision making utilized in Adyen Capital'
  }
]

const subjectTypesOptions = [
  {
    id: 'marketing',
    value: 'SubjectType4',
    text: 'Newsletter subscriber'
  },
  {
    id: 'employee',
    value: 'SubjectType1',
    text: '(Former) Employee'
  },
  {
    id: 'applicant',
    value: 'SubjectType2',
    text: 'Applicant'
  },
  {
    id: 'prospectingCustomer',
    value: 'SubjectType3',
    text: 'Prospecting Customer'
  },
  {
    id: 'customer',
    value: 'SubjectType5',
    text: 'Customer'
  },
  {
    id: 'dataReferenceOther',
    value: 'SubjectType6',
    text: 'Other'
  }
]

const usStatesOptions = [
  { id: 'AL', value: 'Alabama', text: 'Alabama' },
  { id: 'AK', value: 'Alaska', text: 'Alaska' },
  { id: 'AZ', value: 'Arizona', text: 'Arizona' },
  { id: 'AR', value: 'Arkansas', text: 'Arkansas' },
  { id: 'CA', value: 'California', text: 'California' },
  { id: 'CO', value: 'Colorado', text: 'Colorado' },
  { id: 'CT', value: 'Connecticut', text: 'Connecticut' },
  { id: 'DE', value: 'Delaware', text: 'Delaware' },
  { id: 'DC', value: 'District Of Columbia', text: 'District Of Columbia' },
  { id: 'FL', value: 'Florida', text: 'Florida' },
  { id: 'GA', value: 'Georgia', text: 'Georgia' },
  { id: 'HI', value: 'Hawaii', text: 'Hawaii' },
  { id: 'ID', value: 'Idaho', text: 'Idaho' },
  { id: 'IL', value: 'Illinois', text: 'Illinois' },
  { id: 'IN', value: 'Indiana', text: 'Indiana' },
  { id: 'IA', value: 'Iowa', text: 'Iowa' },
  { id: 'KS', value: 'Kansas', text: 'Kansas' },
  { id: 'KY', value: 'Kentucky', text: 'Kentucky' },
  { id: 'LA', value: 'Louisiana', text: 'Louisiana' },
  { id: 'ME', value: 'Maine', text: 'Maine' },
  { id: 'MD', value: 'Maryland', text: 'Maryland' },
  { id: 'MA', value: 'Massachusetts', text: 'Massachusetts' },
  { id: 'MI', value: 'Michigan', text: 'Michigan' },
  { id: 'MN', value: 'Minnesota', text: 'Minnesota' },
  { id: 'MS', value: 'Mississippi', text: 'Mississippi' },
  { id: 'MO', value: 'Missouri', text: 'Missouri' },
  { id: 'MT', value: 'Montana', text: 'Montana' },
  { id: 'NE', value: 'Nebraska', text: 'Nebraska' },
  { id: 'NV', value: 'Nevada', text: 'Nevada' },
  { id: 'NH', value: 'New Hampshire', text: 'New Hampshire' },
  { id: 'NJ', value: 'New Jersey', text: 'New Jersey' },
  { id: 'NM', value: 'New Mexico', text: 'New Mexico' },
  { id: 'NY', value: 'New York', text: 'New York' },
  { id: 'NC', value: 'North Carolina', text: 'North Carolina' },
  { id: 'ND', value: 'North Dakota', text: 'North Dakota' },
  { id: 'OH', value: 'Ohio', text: 'Ohio' },
  { id: 'OK', value: 'Oklahoma', text: 'Oklahoma' },
  { id: 'OR', value: 'Oregon', text: 'Oregon' },
  { id: 'PA', value: 'Pennsylvania', text: 'Pennsylvania' },
  { id: 'RI', value: 'Rhode Island', text: 'Rhode Island' },
  { id: 'SC', value: 'South Carolina', text: 'South Carolina' },
  { id: 'SD', value: 'South Dakota', text: 'South Dakota' },
  { id: 'TN', value: 'Tennessee', text: 'Tennessee' },
  { id: 'TX', value: 'Texas', text: 'Texas' },
  { id: 'UT', value: 'Utah', text: 'Utah' },
  { id: 'VT', value: 'Vermont', text: 'Vermont' },
  { id: 'VA', value: 'Virginia', text: 'Virginia' },
  { id: 'WA', value: 'Washington', text: 'Washington' },
  { id: 'WV', value: 'West Virginia', text: 'West Virginia' },
  { id: 'WI', value: 'Wisconsin', text: 'Wisconsin' },
  { id: 'WY', value: 'Wyoming', text: 'Wyoming' }
]

const formSubmitState = ref(DsFormSubmitState.start)
const formFields = ref([])

const fieldsIndex = computed(() => {
  return Object.fromEntries(
    formFields.value.map((field, index) => {
      return [field.name, index]
    })
  )
})

onBeforeMount(() => {
  const countries = messages['en']?.countries
  const countryOptions = Object.keys(countries).map((country) => ({
    id: country,
    text: countries[country],
    value: countries[country]
  }))

  formFields.value = [
    {
      name: 'countryCode',
      label: 'Country',
      placeholder: 'Select a country',
      type: 'select',
      required: true,
      errorMessage: 'This field is required, select a country',
      options: countryOptions,
      visible: true
    },
    {
      name: 'state',
      label: 'State',
      placeholder: 'Select a state',
      type: 'select',
      required: false,
      options: usStatesOptions,
      visible: false
    },
    {
      name: 'subjectTypes',
      label: 'I am a (an)',
      placeholder: 'Select your profile',
      type: 'select',
      required: true,
      errorMessage: 'This field is required, select a profile type',
      options: subjectTypesOptions,
      labels: {
        results: 'selected',
        ariaLabelHint: 'ariaLabelHint',
        clearValueButton: 'clearValueButton'
      },
      visible: true
    },
    {
      name: 'requestTypes',
      label: 'Select request type',
      placeholder: 'Request type',
      type: 'select',
      required: true,
      errorMessage: 'This field is required, select request type(s)',
      options: requestTypesOptions,
      visible: true
    },
    {
      name: 'firstName',
      label: 'First name',
      type: 'text',
      required: true,
      errorMessage: 'This field is required, enter a valid name',
      visible: true
    },
    {
      name: 'lastName',
      label: 'Last name',
      type: 'text',
      required: true,
      errorMessage: 'This field is required, enter a valid last name',
      visible: true
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      maxLength: '150',
      errorMessage: 'This field is required, enter a valid email address example@domain.ending',
      // Validate email using same regex as in DS but without restricting the domain
      validate: (value) => {
        const regex = new RegExp(
          ['^([\\w+.-]+)@(\\[(\\d{1,3}\\.){3}|', '(([a-zA-Z\\d-]+\\.)+))([a-zA-Z]{2,63}|\\d{1,3})(\\]?)$'].join('')
        )
        return regex.test(value)
      },
      visible: true
    },
    {
      name: 'registrationId',
      label: 'Registration ID',
      type: 'text',
      required: false,
      visible: false
    },
    {
      name: 'requestDetails',
      label: 'Request Details',
      infoMessage:
        'Please Provide Us with More Details - We appreciate your request, but will need some more information to ensure that we fulfill it properly. Please provide some more details about your request in the box below.',
      type: 'textarea',
      required: true,
      visible: true
    }
  ]
})

onMounted(async () => {
  try {
    await nextTick()
    await initRecaptcha()
  } catch (e) {
    console.error(e)
  }
})

// onBeforeUnmount(() => {
//   recaptcha.destroy()
// })

const handleFormChange = ({ fieldName, fieldValue }) => {
  // Show states if country is USA
  if (fieldName === 'countryCode') {
    formFields.value[fieldsIndex.value['state']].visible = fieldValue === 'United States'
  }
  // Show registrationId field depending on type of request
  if (fieldName === 'requestTypes') {
    // For request type with 'Review Automated Decision' or 'Opt-out for automated decision making utilized in Adyen Capital'
    const isCustomerCustomRequest = fieldValue === 'RequestType7' || fieldValue === 'RequestType12'
    formFields.value[fieldsIndex.value['registrationId']].visible = isCustomerCustomRequest
    formFields.value[fieldsIndex.value['registrationId']].required = isCustomerCustomRequest
  }
  // Show custom request options if 'customer' is selected, and switch back to default if another subject type is selected
  if (fieldName === 'subjectTypes' && fieldValue === 'SubjectType5') {
    formFields.value[fieldsIndex.value['requestTypes']].options = requestTypesCustomOptions
  } else if (fieldName === 'subjectTypes') {
    formFields.value[fieldsIndex.value['requestTypes']].options = requestTypesOptions
  }
}

const handleSubmit = async ({ formData }) => {
  formSubmitState.value = DsFormSubmitState.loading

  try {
    // re-captcha validation
    let recaptchaToken
    try {
      recaptchaToken = await recaptcha.value.execute('submit')
      if (!recaptchaToken) {
        console.error('No recaptcha token')
        return
      }
    } catch {
      throw new Error('Error: re-captcha validation failed')
    }

    const data = {
      recaptchaToken,
      payload: {
        ...formData,
        language: 'en-us'
      }
    }

    const response = await $fetch('/api/v3/main_core/privacy-form-submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    const parsedResponse = JSON.parse(response?.body)

    if (!parsedResponse?.success) {
      formSubmitState.value = DsFormSubmitState.fail
      throw new Error('Error: form submission fails')
    }
    formSubmitState.value = DsFormSubmitState.success

    // Redirect in case a path is provided
    if (props.formSuccessRedirectPath) {
      router.replace({ name: `${props.formSuccessRedirectPath}` })
    }
  } catch (error) {
    console.error('Form Error', error)
    formSubmitState.value = DsFormSubmitState.fail
  }
}
</script>
